import { useState, useEffect, useMemo } from 'react';
import { useUserStore } from '../store/user';
import { api } from '../services/api';

export interface Permission {
  name: string;
  key: string;
  description: string;
}

export interface UserPermissions {
  role: string;
  permissions: Permission[];
}

export const usePermissions = () => {
  const { user } = useUserStore();
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      // Usar fallback para TODAS as roles para evitar problemas com API
      setPermissions({
        role: user.role,
        permissions: [] // Será tratado nas funções específicas
      });
      setLoading(false);
    };

    fetchPermissions();
  }, [user]);

  const hasPermission = (permission: string): boolean => {
    if (!permissions) return false;
    return permissions.permissions.some(p => p.key === permission);
  };

  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  const canCreateDocuments = useMemo(() => {
    // Para usuários admin/manager, sempre permitir
    if (user && (user.role === 'admin' || user.role === 'manager')) {
      return true;
    }
    // Usuários digitizer podem criar documentos
    if (user && user.role === 'digitizer') {
      return true;
    }
    // Usuários user e viewer não podem criar documentos
    if (user && (user.role === 'user' || user.role === 'viewer')) {
      return false;
    }
    return hasPermission('documents.create');
  }, [permissions, user]);

  const canEditDocuments = useMemo(() => {
    // Para usuários admin/manager, sempre permitir
    if (user && (user.role === 'admin' || user.role === 'manager')) {
      return true;
    }
    // Usuários digitizer podem editar documentos
    if (user && user.role === 'digitizer') {
      return true;
    }
    // Usuários user e viewer não podem editar documentos
    if (user && (user.role === 'user' || user.role === 'viewer')) {
      return false;
    }
    return hasPermission('documents.edit');
  }, [permissions, user]);

  const canDeleteDocuments = useMemo(() => {
    // Somente admin e manager podem excluir (mesma regra aplicada no backend)
    return !!user && (user.role === 'admin' || user.role === 'manager');
  }, [permissions, user]);

  const canViewDocuments = useMemo(() => {
    // Todas as roles podem visualizar documentos
    if (user) {
      return true;
    }
    return hasPermission('documents.view');
  }, [permissions, user]);

  const canDownloadDocuments = useMemo(() => {
    // Todas as roles podem baixar documentos
    if (user) {
      return true;
    }
    return hasPermission('documents.download');
  }, [permissions, user]);

  const canManageUsers = useMemo(() => {
    // Apenas admin e manager podem gerenciar usuários
    if (!user) return false;
    return user.role === 'admin' || user.role === 'manager';
  }, [user]);

  const canManageGroups = useMemo(() => {
    // Apenas admin e manager podem gerenciar grupos
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'manager') {
      return true;
    }
    return hasPermission('groups.create') || hasPermission('groups.edit') || hasPermission('groups.delete');
  }, [permissions, user]);

  const canManageDocumentTypes = useMemo(() => {
    // Apenas admin pode gerenciar tipos de documento
    if (!user) return false;
    if (user.role === 'admin') {
      return true;
    }
    return hasPermission('documentTypes.create') || hasPermission('documentTypes.edit') || hasPermission('documentTypes.delete');
  }, [permissions, user]);

  const canViewLogs = useMemo(() => {
    // Apenas admin pode visualizar logs
    if (!user) return false;
    if (user.role === 'admin') {
      return true;
    }
    return hasPermission('logs.view');
  }, [permissions, user]);

  const canViewReports = useMemo(() => {
    // Apenas admin e manager podem visualizar relatórios
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'manager') {
      return true;
    }
    return hasPermission('reports.view');
  }, [permissions, user]);

  const canEditPermissions = useMemo(() => {
    // Apenas admin e manager podem editar permissões
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'manager') {
      return true;
    }
    return hasPermission('documents.permissions.edit');
  }, [permissions, user]);

  return {
    permissions,
    loading,
    hasPermission,
    hasRole,
    hasAnyRole,
    canCreateDocuments,
    canEditDocuments,
    canDeleteDocuments,
    canViewDocuments,
    canDownloadDocuments,
    canManageUsers,
    canManageGroups,
    canManageDocumentTypes,
    canViewLogs,
    canViewReports,
    canEditPermissions,
  };
};
